/* eslint-disable boundaries/dependencies */ import React, { useRef, useState, useMemo, useEffect } from 'react';
import { UIGameCardStack } from '../UIGameCardStack/UIGameCardStack.tsx';
import clsx from 'clsx';
import styles from './UIGameCardInventory.module.scss';
import { CardDataController } from '@colonist/game-card-types';
import { UIGameCardViewInventory } from '../../../../../code/client/shared/scripts/ui_game/ui_game_card/UIGameCardViewInventory.ts';
import { CardValidatorUnordered } from '@colonist/game-mechanic-validators';
import { ArrayUtils, clamp } from '@colonist/utils';
import { CARD_RATIO, getIfCardHasBadgeFromType } from '@colonist/ui-game';
function getTotalWidthOfCards(numberOfStacks, widthCausedByUniqueCards, numberOfSameType, cardGapBetweenSameType, cardGapBetweenTypes, numberOfPartitions, cardWidth, partitionWidth) {
    const widthCausedBySimilarStackedCards = numberOfSameType * (cardWidth + cardGapBetweenSameType);
    const widthCausedByPartition = numberOfPartitions * (partitionWidth + cardGapBetweenTypes);
    const widthCausedByGapBetweenStacks = (numberOfStacks - 1) * cardGapBetweenTypes;
    const totalWidthOfCards = widthCausedByUniqueCards + widthCausedBySimilarStackedCards + widthCausedByGapBetweenStacks + widthCausedByPartition;
    return totalWidthOfCards;
}
function recomputeGapsForVerticalStacking(cardWidth, cardHeight, numberOfStacks, partitionCount, partitionWidth, containerWidth, defaultGapBetweenStacks, verticalOverlapRatio, setCardGaps) {
    const verticalGapWithinStack = -cardHeight * verticalOverlapRatio;
    const widthForPartitions = partitionCount * (partitionWidth + defaultGapBetweenStacks);
    const widthForAllStacks = numberOfStacks * cardWidth;
    const widthForDefaultGaps = (numberOfStacks - 1) * defaultGapBetweenStacks;
    const totalWidthNeeded = widthForAllStacks + widthForDefaultGaps + widthForPartitions;
    const stacksFitWithoutOverlap = totalWidthNeeded <= containerWidth;
    if (stacksFitWithoutOverlap) {
        return setCardGaps(verticalGapWithinStack, defaultGapBetweenStacks);
    }
    const widthAvailableForGaps = containerWidth - widthForAllStacks - widthForPartitions;
    const numberOfGapsBetweenStacks = numberOfStacks - 1;
    const horizontalGapBetweenStacks = numberOfGapsBetweenStacks > 0 ? widthAvailableForGaps / numberOfGapsBetweenStacks : defaultGapBetweenStacks;
    setCardGaps(verticalGapWithinStack, horizontalGapBetweenStacks);
}
function recomputeGapsForHorizontalStacking(defaultGapBetweenSameType, defaultGapBetweenTypes, partitionWidth, numberOfStacks, partitionCount, cardWidth, inventoryWidth, totalNumberOfCards, setCardGaps) {
    let nextCardGapBetweenSameType = defaultGapBetweenSameType;
    let nextCardGapBetweenTypes = defaultGapBetweenTypes;
    const numberOfSameType = totalNumberOfCards - numberOfStacks;
    const widthCausedByPartition = partitionCount * partitionWidth;
    const widthCausedByHavingUniqueCards = numberOfStacks * cardWidth;
    const totalWidthOfCardsInInventory = getTotalWidthOfCards(numberOfStacks, widthCausedByHavingUniqueCards, numberOfSameType, nextCardGapBetweenSameType, nextCardGapBetweenTypes, partitionCount, cardWidth, partitionWidth);
    const areCardsWithinContainerBounds = totalWidthOfCardsInInventory <= inventoryWidth;
    if (areCardsWithinContainerBounds) return setCardGaps(nextCardGapBetweenSameType, nextCardGapBetweenTypes);
    const pixelOffsetForAllStacks = (numberOfStacks - 1) * 1;
    const widthCausedByAllCards = totalNumberOfCards * cardWidth;
    const widthCausedByGapBetweenTypes = (numberOfStacks - 1 + partitionCount) * nextCardGapBetweenTypes;
    const totalWidthDedicatedToCardsOfSameType = inventoryWidth - widthCausedByAllCards - widthCausedByPartition - widthCausedByGapBetweenTypes;
    nextCardGapBetweenSameType = (totalWidthDedicatedToCardsOfSameType - pixelOffsetForAllStacks) / numberOfSameType;
    nextCardGapBetweenSameType = clamp(nextCardGapBetweenSameType, -cardWidth, defaultGapBetweenSameType);
    const totalWidthOfCardsAfterGapApplied = getTotalWidthOfCards(numberOfStacks, widthCausedByHavingUniqueCards, numberOfSameType, nextCardGapBetweenSameType, nextCardGapBetweenTypes, partitionCount, cardWidth, partitionWidth);
    const widthPixelTolerance = 0.5;
    const doCardsOfDifferentTypeNeedToOverlap = totalWidthOfCardsAfterGapApplied > inventoryWidth + widthPixelTolerance;
    if (!doCardsOfDifferentTypeNeedToOverlap) return setCardGaps(nextCardGapBetweenSameType, nextCardGapBetweenTypes);
    const totalWidthDedicatedToCardsOfDifferentType = inventoryWidth - widthCausedByHavingUniqueCards - widthCausedByPartition;
    const numberOfGapsBetweenTypes = numberOfStacks - partitionCount;
    nextCardGapBetweenTypes = totalWidthDedicatedToCardsOfDifferentType / numberOfGapsBetweenTypes;
    setCardGaps(nextCardGapBetweenSameType, nextCardGapBetweenTypes);
}
export const UIGameCardInventory = ({ cardState, cardStackSizes = cardState, cardsToDisable, cardsToGreyOut, cardsToHighlight, cardsToSuggestion, onClickCard, className, showTipTitle = true, showTipBody = true, tooltipPosition = 'top', enablePartition = true, forceShowCardBadges, animate = true, bankTradeRatiosState, cardsToScaleOnHover, cardsHelperText, cardSize = 'base', cardStackDirection = 'horizontal', id, cardsToShowTooltip, cardTooltipOverrides, cardsToHideBadge })=>{
    const DEFAULT_GAP_BETWEEN_STACKS = 0.1;
    const CARD_ASPECT_RATIO = 1 / CARD_RATIO;
    const PARTITION_WIDTH = 6;
    const VERTICAL_OVERLAP_RATIO = 0.9;
    const HORIZONTAL_OVERLAP_RATIO = 0.8;
    const isVerticalDirection = cardStackDirection === 'vertical';
    const { cardValidator, uniqueCardEnums, partitionIndexes } = useMemo(()=>{
        const cardValidatorUnordered = new CardValidatorUnordered(cardState);
        const uniqueCards = ArrayUtils.removeDuplicatesFromArray(cardValidatorUnordered.cards);
        const sortedUniqueCards = CardDataController.sortCards(uniqueCards);
        const uniqueCardDatas = sortedUniqueCards.map((cardEnum)=>CardDataController.getCardDataForCard(cardEnum));
        const filteredCardDatas = uniqueCardDatas.filter((cardData)=>cardData != undefined);
        const partitionIndexesToReturn = UIGameCardViewInventory.getPartitionCardIndexes(filteredCardDatas);
        return {
            cardValidator: cardValidatorUnordered,
            uniqueCardEnums: sortedUniqueCards,
            partitionIndexes: partitionIndexesToReturn
        };
    }, [
        cardState
    ]);
    const [gapWithinStack, setGapWithinStack] = useState(-26);
    const [gapBetweenStacks, setGapBetweenStacks] = useState(DEFAULT_GAP_BETWEEN_STACKS);
    const [naturalWidth, setNaturalWidth] = useState(undefined);
    const containerRef = useRef(null);
    const setCardGaps = (nextGapWithinStack, nextGapBetweenStacks)=>{
        setGapWithinStack(nextGapWithinStack);
        setGapBetweenStacks(nextGapBetweenStacks);
    };
    useEffect(()=>{
        const element = containerRef.current;
        if (!element) return;
        const sample = element.firstElementChild;
        if (!sample) return;
        const recomputeCallback = ()=>{
            const inventoryDocumentWidth = element.getBoundingClientRect().width;
            const pixelCorrection = 1;
            const inventoryWidth = inventoryDocumentWidth - pixelCorrection;
            const totalNumberOfCards = cardValidator.getNumberOfCardsInInventory();
            const numberOfStacks = uniqueCardEnums.length;
            const partitionCount = enablePartition ? partitionIndexes.length : 0;
            if (isVerticalDirection) {
                const cardElement = sample.querySelector('[data-card-enum]');
                if (!cardElement) return;
                const cardRect = cardElement.getBoundingClientRect();
                const widthForPartitions = partitionCount * (PARTITION_WIDTH + DEFAULT_GAP_BETWEEN_STACKS);
                const calculatedNaturalWidth = numberOfStacks * cardRect.width + (numberOfStacks - 1) * DEFAULT_GAP_BETWEEN_STACKS + widthForPartitions;
                setNaturalWidth(calculatedNaturalWidth);
                recomputeGapsForVerticalStacking(cardRect.width, cardRect.height, numberOfStacks, partitionCount, PARTITION_WIDTH, inventoryWidth, DEFAULT_GAP_BETWEEN_STACKS, VERTICAL_OVERLAP_RATIO, setCardGaps);
                return;
            }
            const sampleHeight = sample.getBoundingClientRect().height;
            const cardWidth = sampleHeight * CARD_ASPECT_RATIO;
            const defaultGapBetweenSameType = -cardWidth * HORIZONTAL_OVERLAP_RATIO;
            recomputeGapsForHorizontalStacking(defaultGapBetweenSameType, DEFAULT_GAP_BETWEEN_STACKS, PARTITION_WIDTH, numberOfStacks, partitionCount, cardWidth, inventoryWidth, totalNumberOfCards, setCardGaps);
        };
        recomputeCallback();
        const resizeObserver = new ResizeObserver(()=>recomputeCallback());
        resizeObserver.observe(element);
        return ()=>resizeObserver.disconnect();
    }, [
        cardState,
        partitionIndexes,
        enablePartition,
        uniqueCardEnums.length,
        isVerticalDirection
    ]);
    const containerStyle = {
        '--outer-gap': `${gapBetweenStacks}px`,
        ...naturalWidth !== undefined && {
            '--natural-width': `${naturalWidth}px`
        }
    };
    return /*#__PURE__*/ React.createElement("div", {
        id: id,
        className: clsx(styles.container, {
            [styles.vertical]: isVerticalDirection
        }, className),
        ref: containerRef,
        style: containerStyle
    }, uniqueCardEnums.map((cardEnum, index)=>{
        const count = cardValidator.getCardCountForCard(cardEnum);
        var _cardsToDisable_cardEnum;
        const numberOfCardsToDisable = (_cardsToDisable_cardEnum = cardsToDisable === null || cardsToDisable === void 0 ? void 0 : cardsToDisable[cardEnum]) !== null && _cardsToDisable_cardEnum !== void 0 ? _cardsToDisable_cardEnum : 0;
        var _cardsToGreyOut_cardEnum;
        const numberOfCardsToGreyOut = (_cardsToGreyOut_cardEnum = cardsToGreyOut === null || cardsToGreyOut === void 0 ? void 0 : cardsToGreyOut[cardEnum]) !== null && _cardsToGreyOut_cardEnum !== void 0 ? _cardsToGreyOut_cardEnum : 0;
        const numberOfCardsToHighlight = (cardsToHighlight === null || cardsToHighlight === void 0 ? void 0 : cardsToHighlight.includes(cardEnum)) ? count : 0;
        const numberOfCardsToSuggestion = (cardsToSuggestion === null || cardsToSuggestion === void 0 ? void 0 : cardsToSuggestion.includes(cardEnum)) ? count : 0;
        const shouldShowPartition = enablePartition && partitionIndexes.includes(index);
        var _cardsToScaleOnHover_includes;
        const shouldScaleOnHover = (_cardsToScaleOnHover_includes = cardsToScaleOnHover === null || cardsToScaleOnHover === void 0 ? void 0 : cardsToScaleOnHover.includes(cardEnum)) !== null && _cardsToScaleOnHover_includes !== void 0 ? _cardsToScaleOnHover_includes : false;
        const helperText = cardsHelperText === null || cardsHelperText === void 0 ? void 0 : cardsHelperText[cardEnum];
        const cardTypeEnum = CardDataController.getCardTypeFromCardEnum(cardEnum);
        if (cardTypeEnum == undefined) return;
        const tradeRatio = bankTradeRatiosState === null || bankTradeRatiosState === void 0 ? void 0 : bankTradeRatiosState[cardEnum];
        var _cardsToHideBadge_includes;
        const isCardBadgeHidden = (_cardsToHideBadge_includes = cardsToHideBadge === null || cardsToHideBadge === void 0 ? void 0 : cardsToHideBadge.includes(cardEnum)) !== null && _cardsToHideBadge_includes !== void 0 ? _cardsToHideBadge_includes : false;
        const cardHasBadge = getIfCardHasBadgeFromType(cardTypeEnum, count);
        const shouldShowBadge = isCardBadgeHidden ? false : forceShowCardBadges === undefined ? cardHasBadge : forceShowCardBadges;
        const shouldShowTooltipForThisCard = cardsToShowTooltip === undefined ? true : cardsToShowTooltip.includes(cardEnum);
        const showTipTitleForCard = showTipTitle && shouldShowTooltipForThisCard;
        const showTipBodyForCard = showTipBody && shouldShowTooltipForThisCard;
        return /*#__PURE__*/ React.createElement(React.Fragment, {
            key: cardEnum
        }, shouldShowPartition && /*#__PURE__*/ React.createElement("div", {
            className: clsx(styles.partition, styles[cardSize])
        }), /*#__PURE__*/ React.createElement(UIGameCardStack, {
            cardEnum: cardEnum,
            count: count,
            stackMax: cardStackSizes === null || cardStackSizes === void 0 ? void 0 : cardStackSizes[cardEnum],
            gap: gapWithinStack,
            hideBadge: !shouldShowBadge,
            ...onClickCard && {
                onClick: onClickCard
            },
            showTipBody: showTipBodyForCard,
            showTipTitle: showTipTitleForCard,
            stackDirection: cardStackDirection,
            tooltipPosition: tooltipPosition,
            animate: animate,
            numberOfCardsToDisable: numberOfCardsToDisable,
            numberOfCardsToGreyOut: numberOfCardsToGreyOut,
            tradeRatio: tradeRatio,
            numberOfCardsToHighlight: numberOfCardsToHighlight,
            numberOfCardsToSuggestion: numberOfCardsToSuggestion,
            className: styles.stackWrapper,
            zIndex: uniqueCardEnums.length - index,
            scaleOnHover: shouldScaleOnHover,
            helperText: helperText,
            size: cardSize,
            ...(cardTooltipOverrides === null || cardTooltipOverrides === void 0 ? void 0 : cardTooltipOverrides[cardEnum]) && {
                customTooltip: cardTooltipOverrides[cardEnum]
            }
        }));
    }));
};
